import type { SessionStepDto } from '../../schemas/sessionSchemas';

/**
 * The Email Verification step. Matched on step type alone — like Questionnaire and Document
 * Template, and unlike the verification steps whose display names vary, this one is only ever
 * created by the builder's Email Verification tile.
 */
export function isEmailVerificationStep(step: SessionStepDto): boolean {
  return step.stepType === 'email_verification';
}

/**
 * What the step's `resultJson` holds once it passes.
 *
 * Written entirely from the server-side challenge, never from the applicant's posted body — see the
 * `email_verification` branch in `SubmitStepDataCommandHandler`. Every field is optional here because
 * Return Data can switch any of them off, and because this also renders rows written by earlier
 * builds.
 */
export interface EmailVerificationResult {
  email_address?: string;
  verified?: boolean;
  verified_at?: string;
  codes_sent?: number;
}

/**
 * Reads the result back out of a stored step.
 *
 * Tolerant: returns an empty object for absent, unparseable or wrongly-shaped JSON. This runs on the
 * admin's session view, where a detail panel that throws takes the whole page with it.
 */
export function parseEmailVerificationResult(resultJson: string | null): EmailVerificationResult {
  if (!resultJson) return {};

  try {
    const parsed: unknown = JSON.parse(resultJson);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as EmailVerificationResult;
  } catch {
    return {};
  }
}

/**
 * `soham@signcare.io` → `s***m@signcare.io`.
 *
 * Mirrors the server's `ApplicantEmailAddress.Mask` so the applicant sees the same masking whether
 * the string came from a fresh send response or was re-derived locally after a resume. Not a
 * security control — the applicant just typed the address — it exists so the confirmation screen
 * does not print a full address back on a screen someone may be looking over.
 */
export function maskEmail(email: string): string {
  const at = email.lastIndexOf('@');
  if (at <= 0) return '***';

  const local = email.slice(0, at);
  const domain = email.slice(at);
  return local.length <= 2 ? `***${domain}` : `${local[0]}***${local[local.length - 1]}${domain}`;
}

/**
 * Length limits from RFC 5321. The total is the server's ceiling too
 * (`ApplicantEmailAddress.MaxLength`).
 */
const MAX_TOTAL = 254;
const MAX_LOCAL = 64;
const MAX_LABEL = 63;

/**
 * Characters legal in an unquoted (dot-atom) local part: RFC 5322's `atext`, plus any Unicode letter
 * or mark so internationalised addresses are accepted.
 *
 * `\p{L}\p{M}\p{N}` rather than `A-Za-z0-9` is the whole point of "all world formats" — without it
 * `पूजा@example.com` and `josé@example.es` would be rejected as junk.
 */
const LOCAL_CHAR = /^[\p{L}\p{M}\p{N}!#$%&'*+/=?^_`{|}~-]+$/u;

/** A domain label: Unicode letters/digits and hyphens, but never starting or ending with a hyphen. */
const DOMAIN_LABEL = /^[\p{L}\p{N}](?:[\p{L}\p{N}-]*[\p{L}\p{N}])?$/u;

/** A TLD must be letters — `example.123` and `example.c0m` are not real destinations. */
const TLD = /^\p{L}{2,}$/u;

/**
 * Full address validation, run on every keystroke so Continue can stay disabled until the address is
 * actually usable.
 *
 * WHY NOT ONE REGEX. A genuinely RFC-complete email regex is thousands of characters and unreadable,
 * and every short one in circulation gets it wrong in one of two directions — the popular ones reject
 * legal addresses (anything with `+`, a Unicode local part, or a long new TLD). Checking the parts in
 * sequence is both correct and something the next person can actually read and change.
 *
 * WHAT IT ACCEPTS: every ordinary worldwide address, including `+` tags, dots and the RFC's odd-but
 * -legal punctuation in the local part, Unicode local parts and internationalised (IDN) domains, and
 * any TLD length.
 *
 * WHAT IT REJECTS: plain text with no `@`, a missing local part or domain, a domain with no dot,
 * consecutive or edge dots, hyphen-edged labels, numeric TLDs, spaces, and over-length parts.
 *
 * TWO LEGAL-BUT-UNSUPPORTED FORMS, deliberately: quoted local parts (`"a b"@x.com`) and IP-literal
 * domains (`a@[192.168.0.1]`). Both are valid RFC 5322 and neither is deliverable at any real mail
 * provider, so accepting them would only let an applicant enter an address they can never read.
 *
 * The server's `MailAddress` parse remains the authority; this exists so the applicant is told
 * immediately instead of after a round trip.
 */
export function validateEmail(value: string): string | null {
  const v = value.trim();
  if (!v) return 'Enter your email address.';
  if (/\s/.test(v)) return "An email address can't contain spaces.";
  if (v.length > MAX_TOTAL) return 'That email address is too long.';

  // Split on the LAST "@": everything before it is the local part. Quoted local parts (which may
  // themselves contain "@") are not supported — see the note above.
  const at = v.lastIndexOf('@');
  if (at < 0) return 'Enter a valid email address, including the @ symbol.';

  const local = v.slice(0, at);
  const domain = v.slice(at + 1);

  if (!local) return 'Enter the part before the @.';
  if (local.length > MAX_LOCAL) return 'The part before the @ is too long.';
  if (local.includes('@')) return 'An email address can only contain one @ symbol.';

  // Dots are legal INSIDE the local part but not at either edge, and never doubled.
  if (local.startsWith('.') || local.endsWith('.')) {
    return "The part before the @ can't start or end with a dot.";
  }
  if (local.includes('..')) return "The part before the @ can't contain two dots in a row.";
  for (const chunk of local.split('.')) {
    if (!LOCAL_CHAR.test(chunk)) {
      return 'The part before the @ contains a character that isn’t allowed.';
    }
  }

  if (!domain) return 'Enter the domain after the @, like example.com.';
  const labels = domain.split('.');
  if (labels.length < 2) return 'Enter a full domain after the @, like example.com.';
  if (labels.some((l) => l.length === 0)) {
    return "The domain can't have an empty part or two dots in a row.";
  }
  if (labels.some((l) => l.length > MAX_LABEL)) return 'Part of that domain is too long.';
  if (labels.some((l) => !DOMAIN_LABEL.test(l))) {
    return 'Enter a valid domain after the @, like example.com.';
  }

  // Non-null: `labels` has at least 2 entries by the check above.
  if (!TLD.test(labels[labels.length - 1] as string)) {
    return 'The domain needs to end in a valid extension, like .com or .in.';
  }

  return null;
}
